import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { projectsAPI } from '@/api/projects';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowLeft, Eye, Plus, Trash2, X } from 'lucide-react';
import { formatCurrency, formatDateShort, getHealthEmoji, getHealthText, getStageNameRussian } from '@/utils/formatters';
import CreateProjectDialog from '@/components/CreateProjectDialog';

const ProjectsPage = ({ createMode = false }) => {
  const { user, hasPageAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  // Initialize filter from URL param or default to 'all'
  const healthParam = searchParams.get('health');
  const [filter, setFilter] = useState(healthParam || 'all'); // all, green, yellow, red
  const [showCreateDialog, setShowCreateDialog] = useState(createMode);
  const [deleting, setDeleting] = useState(null);

  // Get responsible_ids from URL query params
  const responsibleIdsParam = searchParams.get('responsible_ids');

  // Check user permissions based on role and page access matrix
  const userRole = user?.role?.toLowerCase();
  const isAdmin = userRole === 'admin';
  const canCreate = hasPageAccess('/projects/new');

  // Sync filter with URL health param
  useEffect(() => {
    const healthFromUrl = searchParams.get('health');
    if (healthFromUrl && healthFromUrl !== filter) {
      setFilter(healthFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filter !== 'all') {
        filters.health = filter;
      }
      if (responsibleIdsParam) {
        filters.responsible_ids = responsibleIdsParam;
      }
      const data = await projectsAPI.getAll(filters);

      // Health status приходит с backend — НЕ пересчитываем на frontend
      setProjects(data);
    } catch (error) {
      // R1/R4: Заменяем console на toast
      if (error.message && error.message.includes('Network Error')) {
        showError(error, 'Ошибка сети. Проверьте соединение.');
      } else {
        showError(error, 'Ошибка загрузки проектов');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, responsibleIdsParam]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleProjectCreated = () => {
    loadProjects();
    // Если создали из /projects/new - перенаправить на список
    if (createMode) {
      navigate('/projects/list');
    }
  };

  // Обработка закрытия диалога создания
  const handleCreateDialogChange = (open) => {
    setShowCreateDialog(open);
    // Если закрыли диалог из режима создания (/projects/new) - вернуться к списку
    if (!open && createMode) {
      navigate('/projects/list');
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Вы уверены, что хотите удалить проект "${projectName}"?\n\nВсе связанные данные (вехи, этапы) будут удалены безвозвратно.`)) {
      return;
    }

    try {
      setDeleting(projectId);
      await projectsAPI.delete(projectId);
      showSuccess('Проект успешно удалён');
      await loadProjects();
    } catch (error) {
      showError(error, 'Ошибка при удалении проекта');
    } finally {
      setDeleting(null);
    }
  };

  const getHealthBadgeColor = (status) => {
    switch (status) {
      case 'green':
        return 'bg-green-100 text-green-800';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800';
      case 'red':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilterLabel = (filterValue) => {
    switch (filterValue) {
      case 'green':
        return 'В графике';
      case 'yellow':
        return 'Риск';
      case 'red':
        return 'Критично';
      default:
        return filterValue;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="projects-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Назад
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">
                {responsibleIdsParam ? 'Проекты менеджеров' : 'Все проекты'}
              </h1>
              {responsibleIdsParam && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchParams({})}
                  className="flex items-center gap-1 text-gray-600"
                >
                  <X className="w-4 h-4" />
                  Сбросить фильтр
                </Button>
              )}
            </div>
            {canCreate && (
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="flex items-center gap-2"
                data-testid="create-project-button"
              >
                <Plus className="w-4 h-4" />
                Новый проект
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              Все ({projects.length})
            </Button>
            <Button
              variant={filter === 'green' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('green')}
              className={filter === 'green' ? '' : 'border-green-200'}
            >
              🟢 В графике
            </Button>
            <Button
              variant={filter === 'yellow' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('yellow')}
              className={filter === 'yellow' ? '' : 'border-yellow-200'}
            >
              🟡 Риск
            </Button>
            <Button
              variant={filter === 'red' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('red')}
              className={filter === 'red' ? '' : 'border-red-200'}
            >
              🔴 Критично
            </Button>
          </div>
        </div>
      </header>

      {/* Projects List */}
      <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Загрузка проектов...</div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              {filter === 'all' ? 'Проекты не найдены' : `Нет проектов со статусом "${getFilterLabel(filter)}"`}
            </div>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
              >
                Показать все проекты
              </button>
            )}
            {filter === 'all' && (
              <div className="max-w-md mx-auto p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm mt-4">
                <p className="font-semibold text-blue-900 mb-2">ℹ️ Информация</p>
                <p className="text-blue-800 mb-2">
                  В preview окружении страница проектов может не загружать данные из-за особенностей сети.
                </p>
                <p className="text-blue-800">
                  <strong>Все проекты доступны на главном Dashboard</strong> - вернитесь назад, чтобы увидеть полную информацию.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  ← Вернуться к Dashboard
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition-shadow" data-testid="project-card">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{project.name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Клиент: {project.client_name}</p>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge 
                            className={getHealthBadgeColor(project.health_status)}
                            data-testid={`health-badge-${project.id}`}
                          >
                            {getHealthEmoji(project.health_status)} {getHealthText(project.health_status)}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{project.health_reason || 'Статус в норме'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Текущий этап</div>
                      <div className="font-semibold">{getStageNameRussian(project.current_stage)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Бюджет</div>
                      <div className="font-semibold">{formatCurrency(project.finances.total_budget)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Срок завершения</div>
                      <div className="font-semibold">{formatDateShort(project.expected_end_date)}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm">
                      <span className="text-gray-600">Маржа: </span>
                      <span className="font-semibold text-indigo-600">
                        {project.finances.total_margin_percent.toFixed(1)}%
                      </span>
                      <span className="text-gray-500 ml-2">
                        ({formatCurrency(project.finances.total_margin)})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(project.id, project.name);
                          }}
                          disabled={deleting === project.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => navigate(`/projects/${project.id}`, { state: { returnUrl: location.pathname + location.search } })}
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Подробнее
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={showCreateDialog}
        onOpenChange={handleCreateDialogChange}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
};

export default ProjectsPage;
